import type { ApolloError } from '@apollo/client';
import { checkPermissions, permissionsByEntity, useAuth } from '@cmg/auth';
import type { apiTypes } from '@cmg/common';
import { routeUtil } from '@cmg/common';
import { DomainObject, PageLayout } from '@cmg/design-system';
import React, { Fragment, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { useDebouncedCallback } from 'use-debounce';

import type { InvestorCoverageFilters } from '../../../../common/api/rolodexApiClient';
import { useDocumentTitle } from '../../../../common/hooks/useDocumentTitle/useDocumentTitle';
import routeFactory from '../../../../common/util/routeFactory';
import { IdentityPageContent } from '../../../../design-system/IdentityPageContent';
import type { XcEmailSubscriptions } from '../../../../types/domain/self/profile';
import {
  fetchInvestorCoverage,
  fetchProfile,
  fetchUserSettings,
  selectError,
  selectInvestorCoverage,
  selectInvestorCoverageLoading,
  selectPagination,
  selectProfile,
  selectProfileLoading,
  selectSubmitting,
  updateProfile,
  updateUserSettings,
} from '../../profile/ducks';
import { useInvestorCoverageCheck } from '../../profile/hooks/useInvestorCoverageCheck';
import ProfileDomainHighlights from './domain-highlights/ProfileDomainHighlights';
import {
  useUpdateXcNotificationSettingsMutation,
  useXcNotificationSettingQuery,
} from './grid-content/graphql/__generated__';
import { ProfileGridContent } from './grid-content/ProfileGridContent';
import {
  fromXcEmailFormValues,
  toXcEmailInitialValues,
} from './grid-content/XcEmailSubscriptions.model';
import ChangePasswordRoute from './password-change/ChangePasswordRoute';
import { ProfileRouteSkeleton } from './ProfileRouteSkeleton';

const mapStateToProps = state => ({
  profile: selectProfile(state),
  investorCoverage: selectInvestorCoverage(state),
  pagination: selectPagination(state),
  isProfileLoading: selectProfileLoading(state),
  isInvestorCoverageLoading: selectInvestorCoverageLoading(state),
  submitting: selectSubmitting(state),
  error: selectError(state),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchProfile,
      updateProfile,
      fetchUserSettings,
      updateUserSettings,
      fetchInvestorCoverage,
    },
    dispatch
  ),
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export type Props = StateProps & DispatchProps;

const apolloToGenericError = (
  error: ApolloError | undefined | null
): apiTypes.GenericServerError | null => {
  if (!error) return null;

  return {
    message: error.message,
    code: '',
    target: '',
    details: [],
  } satisfies apiTypes.GenericServerError;
};

export const ProfileRouteComponent: React.FC<Props> = ({
  profile,
  investorCoverage,
  submitting,
  error,
  isProfileLoading,
  isInvestorCoverageLoading,
  pagination,
  actions,
}) => {
  const { oidcUserAccountType, oidcUserIdp, userPermissions } = useAuth();
  const canViewHangfire = checkPermissions(userPermissions, [
    permissionsByEntity.HangfireDashboard.READ,
  ]);
  const {
    loading: xcNotificationSettingLoading,
    data: xcNotificationSettingData,
    error: xcNotificationSettingError,
  } = useXcNotificationSettingQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [updateXcNotificationSettings, { loading: updateXcNotificationSettingsLoading }] =
    useUpdateXcNotificationSettingsMutation();

  const xcNotifications = useMemo(
    () => xcNotificationSettingData?.xcNotificationSettings ?? [],
    [xcNotificationSettingData]
  );

  const handleSaveXcEmail = useCallback(
    async (values: XcEmailSubscriptions) => {
      if (!xcNotifications) return;

      const updatedNotifications = fromXcEmailFormValues(values, xcNotifications);

      await updateXcNotificationSettings({
        variables: {
          input: {
            notifications: updatedNotifications,
          },
        },
      });
    },
    [xcNotifications, updateXcNotificationSettings]
  );

  const [filters, setFilters] = React.useState<InvestorCoverageFilters>({});

  const debouncedFetch = useDebouncedCallback(actions.fetchInvestorCoverage, 300);

  const { canFetchInvestorCoverage } = useInvestorCoverageCheck(profile?.configurationStatus);

  useDocumentTitle(routeFactory.profile.getDocumentTitle());

  const handleChangePage = (params: apiTypes.ListParams) => {
    actions.fetchInvestorCoverage(params);
  };

  const handleChangeFilter = (filters: InvestorCoverageFilters, debounce?: boolean) => {
    if (debounce) {
      debouncedFetch({ page: 1, ...filters });
    } else {
      actions.fetchInvestorCoverage({ page: 1, ...filters });
    }
    setFilters(filters);
  };
  React.useEffect(() => {
    actions.fetchProfile();
    actions.fetchUserSettings();
  }, [actions]);

  React.useEffect(() => {
    if (canFetchInvestorCoverage) {
      actions.fetchInvestorCoverage({ page: 1, perPage: 25 });
    }
  }, [actions, canFetchInvestorCoverage]);

  if (isProfileLoading || xcNotificationSettingLoading) {
    return <ProfileRouteSkeleton />;
  }

  return (
    <PageLayout header={<DomainObject domainHighlights={<ProfileDomainHighlights />} />}>
      <IdentityPageContent
        gridContent={
          <Fragment>
            <Routes>
              <Route
                path={routeUtil.getRelativeRoutePath(
                  routeFactory.profile.routePath,
                  routeFactory.changePassword.routePath
                )}
                element={<ChangePasswordRoute />}
              />
            </Routes>
            <ProfileGridContent
              profile={profile}
              isProfileSubmitting={submitting}
              onProfileSubmit={profile => actions.updateProfile({ profile })}
              profileSubmitError={error ?? apolloToGenericError(xcNotificationSettingError)}
              oidcUserIdp={oidcUserIdp}
              oidcUserAccountType={oidcUserAccountType || null}
              showHangfireLinks={canViewHangfire}
              showInvestorCoverage={canFetchInvestorCoverage}
              investorCoverage={investorCoverage}
              isLoadingInvestorCoverage={isInvestorCoverageLoading}
              investorCoveragePagination={pagination}
              investorCoverageFilters={filters}
              onInvestorCoverageChangeFilters={handleChangeFilter}
              onInvestorCoverageChangePage={handleChangePage}
              isSavingXcEmail={updateXcNotificationSettingsLoading}
              onSaveXcEmail={handleSaveXcEmail}
              xcEmailInitialValues={toXcEmailInitialValues(xcNotifications ?? [])}
              xcNotifications={xcNotifications}
            />
          </Fragment>
        }
      />
    </PageLayout>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileRouteComponent);
